import React, {
 useEffect, useState, useRef, Fragment
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import SnmpUserForm from 'routes/SnmpUser/components/SnmpUserForm';
import * as snmpUserActions from 'data/snmp/snmp.actions';
import * as snmpUserSelectors from 'data/snmp/snmp.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { snmpUserTemplate } from 'routes/SnmpUser/templates/snmpUserTemplate';
import { adaptTemplateVisibilityMode } from 'routes/SnmpUser/utils/snmpUserTemplate.adapter';
import { SnmpV3User } from 'data/snmp/snmp.types';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => SnmpV3User;
};

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedSnmpUser: snmpUserSelectors.getSelectedSnmpUser(state),
  snmpUser: snmpUserSelectors.getSnmpUser(state),
  snmpUserTemplateDef: adaptTemplateVisibilityMode(snmpUserTemplate, 'view')
});

const mapDispatch = {
  goToSmnpUserList: snmpUserActions.goToSmnpUserList,
  updateSnmpUser: snmpUserActions.updateSnmpUser,
  createSnmpUser: snmpUserActions.createSnmpUser,
  getSnmpUser: snmpUserActions.getSnmpUser,
  goToSnmpUserEdit: snmpUserActions.goToSnmpUserEdit
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpUserView = ({
  history,
  userId,
  userName,
  isAuditEnabled,
  deliveryFunctionId,
  goToSmnpUserList,
  snmpUserTemplateDef,
  updateSnmpUser,
  goToSnmpUserEdit,
  getSnmpUser,
  snmpUser,
  selectedSnmpUser
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SnmpV3User>>({});
  const [template, setTemplate] = useState(snmpUserTemplateDef);
  const { t } = useTranslation();

  const id = selectedSnmpUser?.id;

  useEffect(() => {
    if (id) {
      getSnmpUser(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getSnmpUser]);

  useEffect(() => {
    if (snmpUser == null || snmpUserTemplateDef == null) {
      return;
    }

    const clonedSnmpUser = cloneDeep(snmpUser);
    const clonedTemplate = cloneDeep(snmpUserTemplateDef);
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate);
    setFormData(clonedSnmpUser);
  }, [snmpUser, snmpUserTemplateDef]);

  const handleClickEdit = () => goToSnmpUserEdit(history);

  const handleCancel = () => goToSmnpUserList(history);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SnmpV3User);

    setFormData({
      ...clonedFormData,
      [fieldName]: value
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <SnmpUserForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (snmpUser == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="SnmpUserView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('snmpUser.view.title'))}
          <div className="read-only">{renderForm()}</div>
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SnmpUserView));
